import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import Select from "react-select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@radix-ui/react-popover";
import CalendarSvg from "../../assets/svg/Calendar.svg";
import CrossSvg from "../../assets/svg/crossNew.svg";
import Filter from "@/assets/v2/svg/filter.svg";
import DownArrow from "../../assets/svg/DownArrow.svg";
import DownArrowWhite from "../../assets/svg/downArrowWhite.svg";
import SearchSvg from "@/assets/v2/svg/search.svg";
import { DateRange } from "react-day-picker";
import dateFormater from "@/helperFuntions/dateFormater";
import { Calendar } from "@/components/ui/calendar";
import InputText from "@/components/common/InputText";
import { Task } from "@/api/mutation/useTaskCreateMutation";
import { FIELDS } from "@/api/types/enums";
import { useSearchParams } from "react-router-dom";
import {
  ProjectDefaultViewEnumValue,
  TaskStatusEnumValue,
} from "@backend/src/schemas/enums";
import useProjectQuery from "@/api/query/useProjectQuery";
import useDebounce from "@/hooks/useDebounce";
import { useTranslation } from "react-i18next";

type Options = { label: string; value: string };

type Filter = {
  tasks: Task[] | undefined;
  fieldToShow: FIELDS[];
  filteredData: (data: Task[] | undefined) => void;
  view?: string;
  appleed?: (data: boolean) => void;
  applyFilter?: () => void;
  small?: boolean;
};

export type TaskFilterRef = {
  getFilterApply: () => void;
};
const TaskFilter = (props: Filter) => {

  const { tasks, fieldToShow } = props;
  const [popOverCLose, setPopOverCLose] = useState(false);
  const [filter, setFilter] = useSearchParams();
  const projects = useProjectQuery();
  const { t } = useTranslation();

  // ─── KEY FIX 1: Always keep a ref to the latest filter ───────────────────
  // This prevents stale closures when ApplyFilter is called inside useEffect
  const filterRef = useRef(filter);
  useEffect(() => {
    filterRef.current = filter;
  }, [filter]);

  // ─── KEY FIX 2: Track whether the filter was changed via UI controls ──────
  // If true → wait for Apply button click, don't auto-run
  // If false (URL navigation / initial load) → auto-run on tasks change
  const isUIFilterPending = useRef(false);

  const flags: Options[] = [
    { label: t("select_flag"), value: "" },
    { label: t("SIGNIFICANT_DELAY"), value: "Red" },
    { label: t("ON_TIME"), value: "Green" },
    { label: t("MODERATE_DELAY"), value: "Orange" },
  ];

  const taskOption: Options[] = [
    { label: t("Both"), value: "" },
    { label: t("parent_task"), value: "1" },
    { label: t("sub_task"), value: "2" },
  ];

  const statusOption: Options[] = enumToOptions(TaskStatusEnumValue);

  function enumToOptions(enumObj: any): Options[] {
    return Object.keys(enumObj)?.map((key) => ({
      label: t(enumObj[key as keyof typeof enumObj]),
      value: enumObj[key as keyof typeof enumObj],
    }));
  }

  const assignedTask = (): Options[] | undefined => {
    const projectManagerData: Options[] | undefined = [
      { label: t("select_assigned_user"), value: "" },
    ];
    tasks?.forEach((item) => {
      item.assignedUsers?.forEach((user) => {
        const val = user.user.email;
        const username =
          user.user.firstName && user.user.lastName
            ? user.user.firstName + " " + user.user.lastName
            : val;
        if (!projectManagerData.some((i) => i.value === user.user.email)) {
          projectManagerData.push({ label: username, value: val });
        }
      });
    });
    return projectManagerData;
  };

  const assignedProject = (): Options[] | undefined => {
    const projectData: Options[] | undefined = [
      { label: t("select_project"), value: "" },
    ];
    tasks?.forEach((item) => {
      const project = projects.data?.data.data.find(
        (project) => project.projectId === item.projectId,
      );
      const projectName = project?.projectName || "Project Not Found";
      const projectId = project?.projectId || "ID Not Found";
      const projectIds = new Set(projectData.map((project) => project.value));
      if (!projectIds.has(projectId)) {
        projectData.push({ label: projectName, value: projectId });
      }
    });
    return projectData;
  };

  // ─── KEY FIX 3: tasks change → auto-apply only if filter wasn't changed via UI
  useEffect(() => {
    if (!isUIFilterPending.current) {
      ApplyFilter();
    }
  }, [tasks, filter]);

  const [searchString, setSearchString] = useDebounce<string | undefined>(
    undefined,
    400,
  );

  useEffect(() => {
    let tempData: Task[] = [];
    if (searchString !== undefined) {
      if (
        props.view === ProjectDefaultViewEnumValue.LIST ||
        props.view === ProjectDefaultViewEnumValue.GANTT
      ) {
        tempData = findParentTasksWithTaskName(tasks ?? [], searchString);
      } else {
        tasks?.forEach((element) => {
          const searchStringLower = searchString?.toLowerCase();
          const taskNameLower = element.taskName?.toLowerCase();
          const taskDescriptionLower = element.taskDescription?.toLowerCase();
          if (
            taskNameLower.includes(searchStringLower) ||
            taskDescriptionLower?.includes(searchStringLower)
          ) {
            tempData.push(element);
          }
        });
      }
      props.filteredData(tempData);
    }
  }, [searchString, tasks]);

  const checkTaskName = (task: Task, name: string): boolean => {
    if (task.taskName.toLowerCase().includes(name.toLowerCase())) {
      return true;
    }
    if (task.subtasks && task.subtasks.length > 0) {
      return task.subtasks.some((subtask) => checkTaskName(subtask, name));
    }
    return false;
  };

  function findParentTasksWithTaskName(tasks: Task[], name: string): Task[] {
    const searchName = name.toLowerCase();
    return tasks.filter((task) => checkTaskName(task, searchName));
  }

  const reactSelectStyle = {
    control: (
      provided: Record<string, unknown>,
      state: { isFocused: boolean },
    ) => ({
      ...provided,
      border: "1px solid #E7E7E7",
      paddingTop: "0rem",
      paddingBottom: "0rem",
      outline: state.isFocused ? "2px solid #535EF9" : "0px solid #E7E7E7",
      boxShadow: state.isFocused ? "0px 0px 0px #535EF9" : "none",
      "&:hover": {
        outline: state.isFocused ? "2px solid #535EF9" : "0px solid #E7E7E7",
        boxShadow: "0px 0px 0px #535EF9",
      },
    }),
    option: (
      provided: Record<string, unknown>,
      state: { isSelected: boolean },
    ) => ({
      ...provided,
      color: state.isSelected ? "white" : "black",
      background: state.isSelected ? "#535EF9" : "white",
      "&:hover": {
        color: "white",
        background: "#535ef996",
      },
    }),
  };

  const checkTaskByMilsone = (task: Task) => {
    if (task.subtasks && task.subtasks.length > 0) {
      const index = task.subtasks.findIndex((subtask) => {
        return checkTaskByMilsone(subtask);
      });
      return index !== -1;
    }
    return Boolean(task.milestoneIndicator);
  };

  function findParentTasksWithDoneMilsone(tasks: Task[]) {
    return tasks.filter((task) => {
      return checkTaskByMilsone(task);
    });
  }

  function isDateSevenDays(inputDate: Date): boolean {
    const sevenDaysAgo: Date = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() + 7);
    return new Date(inputDate) <= sevenDaysAgo;
  }

  function isOverDueDays(inputDate: Date): boolean {
    const currentDate: Date = new Date();
    return new Date(inputDate) <= currentDate;
  }

  function isDueTodayDays(inputDate: Date): boolean {
    const currentDate: Date = new Date();
    return dateFormater(new Date(inputDate)) === dateFormater(currentDate);
  }

  const removeDuplicatesById = (arr: Task[]) => {
    const uniqueIds = new Set();
    return arr.filter(
      ({ taskId }) => !uniqueIds.has(taskId) && uniqueIds.add(taskId),
    );
  };

  const checkTaskStatus = (task: Task, status: string): Task | null => {
    let filteredSubtasks: Task[] = [];
    if (task.subtasks && task.subtasks.length > 0) {
      filteredSubtasks = task.subtasks
        .map((subtask) => checkTaskStatus(subtask, status))
        .filter(Boolean) as Task[];
    }
    const isTaskMatch = task.status === status;
    if (isTaskMatch || filteredSubtasks.length > 0) {
      return { ...task, subtasks: filteredSubtasks };
    }
    return null;
  };

  function findParentTasksWithDoneStatus(
    tasks: Task[],
    status: string,
  ): Task[] {
    return tasks
      .map((task) => checkTaskStatus(task, status))
      .filter(Boolean) as Task[];
  }

  // const checkTaskflag = (task: Task, flag: string): Task | null => {
  //   // 1. Recursively process all children first
  //   const filteredSubtasks = (task.subtasks || [])
  //     .map((sub) => checkTaskflag(sub, flag))
  //     .filter((t): t is Task => t !== null);

  //   // 2. Check if the parent itself matches the flag (safely trim strings)
  //   const isTaskMatch = task.flag?.trim() === flag?.trim();

  //   // 3. Keep the task if IT matches OR any of its CHILDREN match
  //   if (isTaskMatch || filteredSubtasks.length > 0) {
  //     const findTask = tasks?.find((t) => t.taskId === task.taskId);
  //     return {
  //       ...findTask ?? task, // Use the original task from the list to preserve all properties
  //       subtasks: filteredSubtasks // Keep matching children (or empty if none match but parent does)
  //     };
  //   }

  //   // 4. If neither matches, drop this branch entirely
  //   return null;
  // };

  // function findParentTasksWithDoneFlag(tasks: Task[], flag: string): Task[] {
  //   return tasks
  //     .map((task) => checkTaskflag(task, flag))
  //     .filter((t): t is Task => t !== null);
  // }

  const [filterApplyed, setFilterApplied] = useState(false);

  const [pendingDateRange, setPendingDateRange] = useState<DateRange | undefined>(
    undefined,
  );

  const syncPendingDateFromUrl = () => {
    const from = filter.get("from");
    const to = filter.get("to");
    if (
      from &&
      to &&
      from !== "undefined" &&
      to !== "undefined" &&
      from !== "null" &&
      to !== "null"
    ) {
      setPendingDateRange({ from: new Date(from), to: new Date(to) });
    } else {
      setPendingDateRange(undefined);
    }
  };

  const prevPopoverOpen = useRef(popOverCLose);
  useEffect(() => {
    const opened = popOverCLose && !prevPopoverOpen.current;
    if (!popOverCLose || opened) {
      syncPendingDateFromUrl();
    }
    prevPopoverOpen.current = popOverCLose;
  }, [popOverCLose, filter]);

  const startOfDayMs = (d: Date) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x.getTime();
  };

  const endOfDayMs = (d: Date) => {
    const x = new Date(d);
    x.setHours(23, 59, 59, 999);
    return x.getTime();
  };

  const taskOverlapsPeriod = (task: Task, from: Date, to: Date) => {
    const rs = startOfDayMs(from);
    const re = endOfDayMs(to);
    const ts = startOfDayMs(new Date(task.startDate ?? ""));
    const te = endOfDayMs(
      new Date(task.endDate ?? task.startDate ?? ""),
    );
    return ts <= re && te >= rs;
  };

  const ApplyFilter = () => {
    const url = new URLSearchParams(filterRef.current);
    if (pendingDateRange?.from) {
      const fromD = pendingDateRange.from;
      const toD = pendingDateRange.to ?? pendingDateRange.from;
      url.set("from", fromD.toISOString());
      url.set("to", toD.toISOString());
    } else {
      url.delete("from");
      url.delete("to");
    }
    filterRef.current = url;
    setFilter(url);
    const currentFilter = url;
    let filteredData = tasks;

    if (currentFilter.get("from") && currentFilter.get("to")) {
      const from = new Date(currentFilter.get("from")!);
      const to = new Date(currentFilter.get("to")!);
      filteredData = filteredData?.filter((d) => taskOverlapsPeriod(d, from, to));
    }

    if (currentFilter && currentFilter.get("assigned")) {
      const assignedEmail = currentFilter.get("assigned")!;
      const result: Task[] = [];

      const pushUnique = (task: Task) => {
        if (!result.some((t) => t.taskId === task.taskId)) {
          result.push(task);
        }
      };

      const containsUserDeep = (task: Task): boolean => {
        if (task.assignedUsers?.some((u) => u.user.email === assignedEmail)) {
          return true;
        }
        if (Array.isArray(task.subtasks)) {
          return task.subtasks.some(containsUserDeep);
        }
        return false;
      };

      const pushParents = (task: Task) => {
        if (!task.parentTaskId) return;
        const parent = filteredData?.find(
          (t) => t.taskId === task.parentTaskId,
        );
        if (parent) {
          pushUnique(parent);
          pushParents(parent);
        }
      };

      filteredData?.forEach((task) => {
        if (containsUserDeep(task)) {
          pushUnique(task);
          pushParents(task);
        }
      });

      filteredData = result;
    }

    if (currentFilter && Boolean(currentFilter.get("flag"))) {
      filteredData = safeFilterByFlag(filteredData, currentFilter.get("flag"), {
        includeParents: true,        // ✅ Include parent when child matches
        validateHierarchy: true,      // ✅ Check for broken refs
        excludeOrphaned: true,        // ✅ Remove orphaned tasks
      });
    }

    if (currentFilter && currentFilter.get("completed")) {
      filteredData = filteredData?.filter(
        (data) =>
          Number(data.completionPecentage) !== 100 ||
          data.status !== TaskStatusEnumValue.COMPLETED,
      );
    }

    if (currentFilter && currentFilter.get("dueSevenDays")) {
      filteredData = filteredData?.filter((data) =>
        isDateSevenDays(data.endDate) && data.status !== TaskStatusEnumValue.COMPLETED && Number(data.completionPecentage || 0) !== 100
      );
    }

    if (currentFilter && currentFilter.get("overdueDays")) {
      filteredData = filteredData?.filter((data) => {
        return isOverDueDays(data.endDate) && data.status !== TaskStatusEnumValue.COMPLETED && Number(data.completionPecentage || 0) !== 100
      }
      );
    }

    if (currentFilter && currentFilter.get("todayDueDays")) {
      filteredData = filteredData?.filter((data) =>
        isDueTodayDays(data.endDate) && data.status !== TaskStatusEnumValue.COMPLETED && Number(data.completionPecentage || 0) !== 100
      );
    }

    if (currentFilter && currentFilter.get("projectId")) {
      filteredData = filteredData?.filter(
        (data) => data.projectId === currentFilter.get("projectId"),
      );
    }

    if (currentFilter && currentFilter.get("milestones")) {
      filteredData = findParentTasksWithDoneMilsone(filteredData ?? []).sort(
        (a, b) =>
          new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
      );
    }

    if (currentFilter.get("status")) {
      filteredData = findParentTasksWithDoneStatus(
        filteredData ?? [],
        currentFilter.get("status") ?? "",
      ).sort(
        (a, b) =>
          new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
      );
    }

    if (currentFilter && currentFilter.get("tasks")) {
      let val;
      if (currentFilter.get("tasks") === "1") {
        val = filteredData?.filter((data) => !data.parentTaskId);
      }
      if (currentFilter.get("tasks") === "2") {
        val = filteredData?.filter((data) => !!data.parentTaskId);
      }
      if (val && val.length > 0) {
        filteredData = val;
      }
    }

    const viewKeys = new Set([
      ProjectDefaultViewEnumValue.GANTT,
      "NEW" + ProjectDefaultViewEnumValue.GANTT,
      ProjectDefaultViewEnumValue.KANBAN,
      ProjectDefaultViewEnumValue.CALENDAR,
      ProjectDefaultViewEnumValue.LIST,
      currentFilter.get("task") ?? "",
      "view",
      "task",
    ]);

    let applyFilter = 0;
    currentFilter.forEach((value, key) => {
      if (!viewKeys.has(key) && !viewKeys.has(value)) {
        // Count only entries that have a real, non-false value
        if (value && value !== "false" && value !== "undefined") {
          applyFilter++;
        }
      }
    });

    if (applyFilter !== 0 && filteredData) {
      setFilterApplied(true);
      filteredData = removeDuplicatesById(filteredData);
      props.appleed && props.appleed(true)
      props.filteredData(filteredData);
      filteredData = []
    } else {
      props.appleed && props.appleed(false)
      props.filteredData(tasks ?? []);
      filteredData = []
      setFilterApplied(false);
    }

    // Reset UI-pending flag and close popover after apply
    isUIFilterPending.current = false;
    setPopOverCLose(false);
  };

  const resetFilter = () => {
    setFilterApplied(false);
    props.appleed && props.appleed(false)
    isUIFilterPending.current = false;

    // ────── FIX: Create a new URLSearchParams instead of passing plain object
    const newFilter = new URLSearchParams();
    const viewValue = filter.get("view");
    if (viewValue) {
      newFilter.set("view", viewValue);
    }
    setFilter(newFilter);
    setPendingDateRange(undefined);

    props.filteredData(tasks ?? []);
  };

  // ───────────────────────────────────────────────────────────────────────────
  // ─── KEY FIX 6 (UPDATED): setParamFilter creates a NEW URLSearchParams ───
  // ───────────────────────────────────────────────────────────────────────────
  const setParamFilter = (key: string, value: string) => {
    isUIFilterPending.current = true; // UI is editing → don't auto-apply

    // CREATE A NEW URLSearchParams object — don't mutate the existing one!
    const newFilter = new URLSearchParams(filter);

    if (!value || value === "undefined" || value === "false") {
      newFilter.delete(key);
    } else {
      newFilter.set(key, value);
    }

    setFilter(newFilter);
  };

  return (
    <div>
      <div className="flex w-full justify-between items-center gap-2">
        <div className="flex justify-between w-full gap-2 text-gray-500">
          <div className="flex justify-between items-center gap-6 px-3 w-full relative">
            <div>
              <Popover open={popOverCLose}>
                <PopoverTrigger className="w-full " asChild>
                  <Button
                    variant={"outline"}
                    className="min-w-28 h-10 rounded-lg px-2"
                    style={{
                      backgroundColor: filterApplyed ? "#535EF9" : "",
                      color: filterApplyed ? "#ffffff" : "",
                    }}
                    onClick={() => setPopOverCLose(!popOverCLose)}
                  >
                    <div className="flex justify-between gap-2 items-center">
                      <div className="flex items-center justify-between gap-1">
                        <div
                          className={`w-fit ${filterApplyed ? "brightness-0 invert" : ""}`}
                        >
                          <img src={Filter} />
                        </div>
                        {t("Filters")}{" "}
                      </div>
                      <div className="w-fit">
                        <img src={filterApplyed ? DownArrowWhite : DownArrow} />
                      </div>
                    </div>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="z-20 absolute bg-white shadow-lg mr-5 max-h-screen -translate-x-[20%]">
                  <div className="p-3 !w-80 flex flex-col gap-3 !z-20">
                    <div>
                      <div className="text-lg font-semibold">
                        {t("Filters")}
                      </div>
                      <hr />
                    </div>
                    <div className="w-full flex flex-col gap-3">
                      {fieldToShow.includes(FIELDS.DUESEVENDAYS) && (
                        <div className="flex justify-between items-center">
                          <div>{t("Due_in_7_day")}</div>
                          <div>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                name="sevenDays"
                                checked={filter.get("dueSevenDays") === "true"}
                                onChange={(e) =>
                                  setParamFilter(
                                    "dueSevenDays",
                                    String(e.target.checked),
                                  )
                                }
                                className="sr-only peer"
                              />
                              <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-secondary dark:peer-focus:ring-secondary rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-secondary"></div>
                            </label>
                          </div>
                        </div>
                      )}
                      {fieldToShow.includes(FIELDS.OVERDUEDAYS) && (
                        <div className="flex justify-between items-center">
                          <div>{t("over_due")}</div>
                          <div>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                name="dueDays"
                                checked={filter.get("overdueDays") === "true"}
                                onChange={(e) =>
                                  setParamFilter(
                                    "overdueDays",
                                    String(e.target.checked),
                                  )
                                }
                                className="sr-only peer"
                              />
                              <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-secondary dark:peer-focus:ring-secondary rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-secondary"></div>
                            </label>
                          </div>
                        </div>
                      )}
                      {fieldToShow.includes(FIELDS.TODAYDUEDAYS) && (
                        <div className="flex justify-between items-center">
                          <div>{t("due_by_today")}</div>
                          <div>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                name="todayDays"
                                checked={filter.get("todayDueDays") === "true"}
                                onChange={(e) =>
                                  setParamFilter(
                                    "todayDueDays",
                                    String(e.target.checked),
                                  )
                                }
                                className="sr-only peer"
                              />
                              <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-secondary dark:peer-focus:ring-secondary rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-secondary"></div>
                            </label>
                          </div>
                        </div>
                      )}
                      {
                        <div className="flex justify-between items-center">
                          <div>{t("milestone")}</div>
                          <div>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                name="milestones"
                                checked={filter.get("milestones") === "true"}
                                onChange={(e) =>
                                  setParamFilter(
                                    "milestones",
                                    String(e.target.checked),
                                  )
                                }
                                className="sr-only peer"
                              />
                              <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-secondary dark:peer-focus:ring-secondary rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-secondary"></div>
                            </label>
                          </div>
                        </div>
                      }
                    </div>
                    <div className="flex justify-between items-center">
                      <div>{t("hide_completed_task")}</div>
                      <div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            name="completed"
                            checked={filter.get("completed") === "true"}
                            onChange={(e) =>
                              setParamFilter(
                                "completed",
                                String(e.target.checked),
                              )
                            }
                            className="sr-only peer"
                          />
                          <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-secondary dark:peer-focus:ring-secondary rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-secondary"></div>
                        </label>
                      </div>
                    </div>
                    {fieldToShow.includes(FIELDS.DATE) && (
                      <div className="w-full">
                        <label className="text-sm font-medium mb-1 block text-gray-700">
                          {t("period")}
                        </label>
                        <Popover>
                          <PopoverTrigger className="w-full" asChild>
                            <Button
                              variant={"outline"}
                              className="w-full h-10 rounded p-0 px-2"
                            >
                              <div className="flex justify-between text-base items-center w-full text-gray-950 font-normal">
                                {pendingDateRange?.from
                                  ? `${dateFormater(pendingDateRange.from)}-${pendingDateRange.to ? dateFormater(pendingDateRange.to) : ""}`
                                  : t("select_period")}
                                <img src={CalendarSvg} width={20} alt="" />
                              </div>
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="z-50 bg-white ">
                            <div>
                              <Calendar
                                mode="range"
                                selected={pendingDateRange}
                                onSelect={(e) => setPendingDateRange(e)}
                                className="rounded-md border"
                                captionLayout="dropdown-buttons"
                                fromYear={2010}
                                toYear={2050}
                              />
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                    )}
                    {fieldToShow.includes(FIELDS.ASSIGNED) && (
                      <div className="w-full">
                        <Select
                          className="p-0 z-40"
                          value={
                            filter.get("assigned")
                              ? {
                                label: filter.get("assigned"),
                                value: filter.get("assigned"),
                              }
                              : {
                                label: t("select_assigned_user"),
                                value: "",
                              }
                          }
                          options={assignedTask()}
                          onChange={(e) => {
                            setParamFilter("assigned", e?.value ?? "");
                          }}
                          placeholder={t("select_assigned_user")}
                          styles={reactSelectStyle}
                        />
                      </div>
                    )}
                    {fieldToShow.includes(FIELDS.FLAGS) && (
                      <div className="w-full">
                        <Select
                          className="p-0 "
                          value={
                            filter.get("flag")
                              ? flags.find(
                                (f) => f.value === filter.get("flag"),
                              )
                              : { label: t("select_flag"), value: "" }
                          }
                          options={flags}
                          onChange={(e) => {
                            setParamFilter("flag", e?.value ?? "");
                          }}
                          placeholder={t("select_flag")}
                          styles={reactSelectStyle}
                        />
                      </div>
                    )}
                    {fieldToShow.includes(FIELDS.TASK) && (
                      <div className="w-full">
                        <Select
                          className="p-0 "
                          value={
                            filter.get("tasks")
                              ? taskOption.find(
                                (d) => d.value === filter.get("tasks"),
                              )
                              : { label: t("Both"), value: "" }
                          }
                          options={taskOption}
                          onChange={(e) => {
                            setParamFilter("tasks", e?.value ?? "");
                          }}
                          placeholder={t("select_task")}
                          styles={reactSelectStyle}
                        />
                      </div>
                    )}
                    {fieldToShow.includes(FIELDS.STATUS) && (
                      <div className="w-full">
                        <Select
                          className="p-0 "
                          value={
                            filter.get("status")
                              ? statusOption.find(
                                (d) => d.value === filter.get("status"),
                              )
                              : { label: t("select_status"), value: "" }
                          }
                          options={[
                            { label: t("select_status"), value: "" },
                            ...statusOption,
                          ]}
                          onChange={(e) => {
                            setParamFilter("status", e?.value ?? "");
                          }}
                          placeholder={t("select_task")}
                          styles={reactSelectStyle}
                        />
                      </div>
                    )}
                    {fieldToShow.includes(FIELDS.PROJECTS) && (
                      <div className="w-full">
                        <Select
                          className="p-0 "
                          value={
                            filter.get("projectId")
                              ? {
                                label: projects.data?.data.data.find(
                                  (project) =>
                                    project.projectId ===
                                    filter.get("projectId"),
                                )?.projectName,
                                value: filter.get("projectId"),
                              }
                              : { label: t("select_project"), value: "" }
                          }
                          options={assignedProject()}
                          onChange={(e) => {
                            setParamFilter("projectId", e?.value ?? "");
                          }}
                          placeholder={t("Select Project")}
                          styles={reactSelectStyle}
                        />
                      </div>
                    )}
                    {fieldToShow.length !== 0 && (
                      <div className="w-full flex gap-2">
                        <Button
                          className="w-full mt-4 border-secondary text-secondary"
                          variant={"outline"}
                          onClick={() => {
                            isUIFilterPending.current = false;
                            setPopOverCLose(false);
                            if (filterApplyed) {
                              resetFilter()
                            }
                          }}
                        >
                          {t("close")}
                        </Button>
                        <Button
                          className="w-full mt-4 text-white"
                          variant={"primary"}
                          onClick={ApplyFilter}
                        >
                          {t("apply")}
                        </Button>
                      </div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            {filterApplyed && (
              <div>
                <Button
                  size={"sm"}
                  className="w-5 h-5 rounded-[50%] px-2 bg-[#EA4335] hover:bg-[#EA4335] absolute left-28 bottom-7"
                  variant={"outline"}
                  onClick={resetFilter}
                >
                  <div className="flex items-center justify-between gap-2 ">
                    <div className="w-2">
                      <img src={CrossSvg} className="m-auto" />
                    </div>
                  </div>
                </Button>
              </div>
            )}
            <div className="w-full flex gap-2 relative items-center">
              <InputText
                className={`pl-11 pr-3 mt-0 h-10 truncate ${props.small ? "!w-36" : "!w-56"} border-slate-200 placeholder:text-sm rounded-md focus:outline-secondary`}
                onChange={(e) => setSearchString(e.target.value)}
                placeholder={t("search_tasks_by_name")}
              />
              <img
                src={SearchSvg}
                className="absolute h-4 left-3 top-3"
                width={"20px"}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TaskFilter;
export function safeFilterByFlag(
  tasks: Task[] | undefined,
  flag: string | undefined | null,
  options?: {
    includeParents?: boolean;
    excludeOrphaned?: boolean;
    validateHierarchy?: boolean;
  }
): Task[] {
  const {
    includeParents = true,
    excludeOrphaned = false,
    validateHierarchy = false,
  } = options || {};

  // Validate input
  if (!tasks || !flag || flag === "undefined" || flag === "") {
    return tasks || [];
  }

  const trimmedFlag = flag.trim();
  const filtered = [...tasks];

  // Step 1: Find direct matches
  const matches = new Set<string>();

  function findMatches(taskList: Task[]) {
    taskList.forEach((task) => {
      if (task.flag?.trim() === trimmedFlag) {
        matches.add(task.taskId);
      }
      if (task.subtasks?.length) {
        findMatches(task.subtasks);
      }
    });
  }

  function addParentIds(taskList: Task[]) {
    taskList.forEach((task) => {
      if (task.subtasks?.length) {
        task.subtasks.forEach((subtask) => {
          if (matches.has(subtask.taskId)) {
            matches.add(task.taskId);
          }
        });
        addParentIds(task.subtasks);
      }
    });
  }

  function buildFiltered(taskList: Task[]): Task[] {
    return taskList
      .filter((task) => matches.has(task.taskId))
      .map((task) => ({
        ...task,
        subtasks: task.subtasks
          ? buildFiltered(task.subtasks)
          : undefined,
      })) as any;
  }

  findMatches(filtered);

  // Step 2: Include parents if requested
  if (includeParents) {
    addParentIds(filtered);
  }

  // Step 3: Build result preserving hierarchy
  const result = buildFiltered(filtered);

  // Optional: Validate hierarchy
  if (validateHierarchy) {
    const orphaned = result.filter((task) => {
      const parentStatus = getParentWithStatus(task, tasks);
      return task.parentTaskId && !parentStatus.parentFound;
    });

    if (orphaned.length > 0) {
      console.warn(`⚠️ Found ${orphaned.length} tasks with missing parents`);
      if (excludeOrphaned) {
        return result.filter(
          (task) => !orphaned.some((o) => o.taskId === task.taskId)
        );
      }
    }
  }

  return result;
}
/**
 * Get the parent task of a given task
 * Searches through the task tree to find the parent
 * @param task - The child task
 * @param allTasks - All tasks (flat array or hierarchical)
 * @returns Parent task if found, null if not found or no parent
 */
export function getParentTask(task: Task, allTasks: Task[]): Task | null {
  // If task has no parent ID, it's a root task
  if (!task.parentTaskId) {
    return null;
  }

  // Search in root level first (most common case)
  const parentInRoot = allTasks.find((t) => t.taskId === task.parentTaskId);
  if (parentInRoot) {
    return parentInRoot;
  }

  // If not found in root, search recursively in subtasks
  const parentInSubtasks = searchParentInSubtasks(
    allTasks,
    task.parentTaskId
  );
  return parentInSubtasks || null;
}

/**
 * Recursively search for a task in the subtasks tree
 * @param tasks - Array of tasks to search
 * @param targetId - The task ID to find
 * @returns Task if found, null otherwise
 */
function searchParentInSubtasks(tasks: Task[], targetId: string): Task | null {
  for (const task of tasks) {
    if (task.taskId === targetId) {
      return task;
    }

    if (task.subtasks && task.subtasks.length > 0) {
      const found = searchParentInSubtasks(task.subtasks, targetId);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Check if a task has a parent and if that parent exists
 * @param task - The task to check
 * @param allTasks - All tasks to search through
 * @returns Object with parent info
 */
export function checkParentAvailability(
  task: Task,
  allTasks: Task[]
): {
  hasParent: boolean;
  parentExists: boolean;
  parentTask: Task | null;
  parentId: string | null;
} {
  const parentId = task.parentTaskId || null;
  const hasParent = !!parentId;

  if (!hasParent) {
    return {
      hasParent: false,
      parentExists: false,
      parentTask: null,
      parentId: null,
    };
  }

  const parentTask = getParentTask(task, allTasks);
  const parentExists = parentTask !== null;

  return {
    hasParent: true,
    parentExists,
    parentTask: parentExists ? parentTask : null,
    parentId,
  };
}

/**
 * Get the full parent chain/hierarchy of a task
 * @param task - The task
 * @param allTasks - All tasks
 * @returns Array of parent tasks from immediate parent to root
 */
export function getParentChain(task: Task, allTasks: Task[]): Task[] {
  const chain: Task[] = [];
  let currentTask = task;

  while (currentTask.parentTaskId) {
    const parent = getParentTask(currentTask, allTasks);
    if (parent) {
      chain.push(parent);
      currentTask = parent;
    } else {
      // Parent ID exists but task not found - broken reference
      break;
    }
  }

  return chain;
}

/**
 * Get parent task with fallback options
 * @param task - The task
 * @param allTasks - All tasks
 * @param fallback - What to return if parent not found (default: null)
 * @returns Parent task or fallback value
 */
export function getParentTaskWithFallback(
  task: Task,
  allTasks: Task[],
  fallback: Task | null = null
): Task | null {
  const parent = getParentTask(task, allTasks);
  return parent || fallback;
}

/**
 * Validate task hierarchy - check for broken parent references
 * @param task - The task to validate
 * @param allTasks - All tasks
 * @returns Validation result
 */
export function validateTaskHierarchy(
  task: Task,
  allTasks: Task[]
): {
  isValid: boolean;
  error: string | null;
  parentTaskId: string | null;
  parentFound: boolean;
} {
  const parentId = task.parentTaskId;

  if (!parentId) {
    return {
      isValid: true,
      error: null,
      parentTaskId: null,
      parentFound: false,
    };
  }

  const parentTask = getParentTask(task, allTasks);

  if (!parentTask) {
    return {
      isValid: false,
      error: `Parent task with ID "${parentId}" not found. Task "${task.taskName}" has a broken parent reference.`,
      parentTaskId: parentId,
      parentFound: false,
    };
  }

  return {
    isValid: true,
    error: null,
    parentTaskId: parentId,
    parentFound: true,
  };
}

/**
 * Get all children of a task (searches entire tree)
 * @param parentTask - The parent task
 * @param allTasks - All tasks to search through
 * @returns Array of all child tasks
 */
export function getAllChildren(
  parentTask: Task,
  allTasks: Task[]
): Task[] {
  const children: Task[] = [];

  function findChildren(parentId: string) {
    // Direct children in root
    const directChildren = allTasks.filter((t) => t.parentTaskId === parentId);
    children.push(...directChildren);

    // Children in subtasks
    for (const task of allTasks) {
      if (task.subtasks) {
        const subtaskChildren = task.subtasks.filter(
          (t) => t.parentTaskId === parentId
        );
        children.push(...subtaskChildren);
      }
    }

    // Recursively find grandchildren
    for (const child of directChildren) {
      findChildren(child.taskId);
    }
  }

  findChildren(parentTask.taskId);
  return children;
}

/**
 * Enhanced function that returns parent with detailed status
 * Perfect for your use case - gives parent if exists, otherwise returns details
 * @param task - The task to find parent for
 * @param allTasks - All tasks in the system
 * @returns Detailed parent information
 */
export function getParentWithStatus(
  task: Task,
  allTasks: Task[]
): {
  parent: Task | null;
  isRootTask: boolean;
  hasParentId: boolean;
  parentFound: boolean;
  parentId: string | null;
  error: string | null;
} {
  const parentId = task.parentTaskId;
  const hasParentId = !!parentId;

  // Task is a root task
  if (!hasParentId) {
    return {
      parent: null,
      isRootTask: true,
      hasParentId: false,
      parentFound: false,
      parentId: null,
      error: null,
    };
  }

  // Try to find the parent
  const parent = getParentTask(task, allTasks);

  if (!parent) {
    return {
      parent: null,
      isRootTask: false,
      hasParentId: true,
      parentFound: false,
      parentId: parentId,
      error: `Parent task "${parentId}" referenced but not found in task list.`,
    };
  }

  return {
    parent,
    isRootTask: false,
    hasParentId: true,
    parentFound: true,
    parentId: parentId,
    error: null,
  };
}